import { objectWithoutProperties as _objectWithoutProperties } from '../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useState, useCallback, useEffect } from 'react';
import IconIcTableEmpty from '@metacrm/metacrm-svg/dist/SvgIcon/svg-icons/IconIcTableEmpty';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { map, get } from 'lodash-es';
import PropTypes from 'prop-types';
import { v4 } from 'uuid';
import Checkbox from '../Checkbox/Checkbox.js';
import CRMTableCell from './CRMTableCell.js';
import DraggableList from './Drag/DraggableList.js';
import Pagination from './Pagination.js';
import { StyledTable, StyledTableHead, StyledTableRow, StyledTableCell, StyledSkeleton, StyledTableBody, StyledRowLoading, StyledTableFoot } from './Table.styles.js';
import LoadingImg from '../assets/img/loading.gif.js';
const _excluded = ["checkbox", "rowData", "contents", "headers", "rowKey", "noKey", "selectedRowData", "setSelectedRowData", "noDataComponent", "containerStyle", "noDataTitle", "onRowClick", "onRequestSort", "rowLoadingText", "order", "orderBy", "isDraggable", "onRemoveHeader", "customTableTop", "isLoading", "LoadingContent", "loadingIndicatorAmount", "verticalAlign", "page", "rowsPerPage", "setPage", "dataCounts", "leftFooter", "pageText", "pageOfText"];
function CRMTable(_ref) {
  let {
    checkbox,
    rowData,
    contents,
    headers,
    rowKey,
    noKey,
    selectedRowData,
    setSelectedRowData,
    noDataComponent,
    containerStyle,
    noDataTitle,
    onRowClick,
    onRequestSort,
    rowLoadingText,
    order,
    orderBy,
    isDraggable,
    onRemoveHeader,
    customTableTop,
    isLoading,
    LoadingContent,
    loadingIndicatorAmount,
    // pagination
    verticalAlign,
    page,
    rowsPerPage,
    setPage,
    dataCounts,
    leftFooter,
    pageText,
    pageOfText
  } = _ref;
  _objectWithoutProperties(_ref, _excluded);
  const [headerItems, setHeaderItems] = useState([]);
  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };
  const hasFixedHeader = headers === null || headers === void 0 ? void 0 : headers.some(item => item.fixed);
  const findLastFixedItem = useCallback(() => {
    const item = map(headers, headerItem => headerItem.fixed).lastIndexOf(true);
    return item;
  }, [headers]);
  useEffect(() => {
    setHeaderItems(headers);
  }, [headers]);
  const lastFixedItem = findLastFixedItem();
  const calculateLeftDistance = index => {
    if (index > lastFixedItem) {
      return null;
    }
    let distance = checkbox ? 40 : 0;
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < index; i++) {
      // eslint-disable-next-line react/prop-types
      distance += headers[i].width;
    }
    return distance;
  };
  const handleCheck = (event, checked, row) => {
    event.stopPropagation();
    if (checked) {
      setSelectedRowData([...selectedRowData, row]);
    } else {
      const filter = [...selectedRowData].filter(value => value._id !== row._id);
      setSelectedRowData(filter);
    }
  };
  const handleAllClick = event => {
    if (event.target.checked) {
      const newSelected = [...selectedRowData];
      rowData.forEach(row => {
        if (!newSelected.some(item => item._id === row._id)) {
          newSelected.push(row);
        }
      });
      setSelectedRowData(newSelected);
    } else {
      const newSelected = selectedRowData.filter(selected => !rowData.some(row => row._id === selected._id));
      setSelectedRowData(newSelected);
    }
  };
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };
  const handleDragEnd = result => {
    const {
      destination,
      source
    } = result;
    if (!destination) return;
    const newItems = reorder(headerItems, source.index, destination.index);
    setHeaderItems(newItems);
  };
  const resetHeaderItems = () => {
    setHeaderItems(headers);
  };
  const handleShowOrHide = result => {
    const newArr = map(headerItems, headerItem => {
      if (headerItem.id === result.id) {
        return result;
      }
      return headerItem;
    });
    setHeaderItems(newArr);
  };
  const allChecked = rowData && rowData.length > 0 && rowData.every(row => selectedRowData.find(item => item[rowKey] === row[rowKey]));
  return /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      position: 'relative'
    }
  }, isDraggable && /*#__PURE__*/React__default.createElement(DraggableList, {
    customTableTop: customTableTop,
    onDragEnd: handleDragEnd,
    items: headerItems,
    onShowOrHide: handleShowOrHide,
    onReset: resetHeaderItems,
    onRemoveHeader: onRemoveHeader
  }), /*#__PURE__*/React__default.createElement(TableContainer, {
    sx: containerStyle
  }, /*#__PURE__*/React__default.createElement(StyledTable, {
    stickyHeader: true,
    sx: {
      minWidth: 650
    },
    "aria-label": "simple table"
  }, /*#__PURE__*/React__default.createElement(StyledTableHead, null, /*#__PURE__*/React__default.createElement(StyledTableRow, null, checkbox && /*#__PURE__*/React__default.createElement(StyledTableCell, {
    cellWidth: 40,
    isHeader: true,
    rows: contents.length,
    isFixed: hasFixedHeader,
    leftDistance: 0
  }, /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }, /*#__PURE__*/React__default.createElement(Checkbox, {
    mr: 0,
    checked: allChecked,
    onChange: handleAllClick,
    inputProps: {
      'aria-label': 'select all desserts'
    }
  }))), headerItems.map((headCell, index) => {
    if (!headCell.show) {
      return null;
    }
    return /*#__PURE__*/React__default.createElement(StyledTableCell, {
      key: headCell.id,
      align: headCell.numeric ? 'right' : 'left',
      padding: headCell.disablePadding ? 'none' : 'normal',
      sortDirection: orderBy === headCell.id ? order : false,
      lastFixedItem: lastFixedItem,
      isHeader: true,
      isFixed: headCell.fixed,
      leftDistance: calculateLeftDistance(index),
      cellWidth: headCell.width,
      maxWidth: headCell.maxWidth,
      rows: contents.length,
      rowIndex: index
    }, headCell.disabledSort ? /*#__PURE__*/React__default.createElement(Box, {
      lineHeight: "20px"
    }, headCell.label) : /*#__PURE__*/React__default.createElement(Box, {
      sx: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        whiteSpace: 'nowrap'
      },
      onClick: createSortHandler(headCell.id)
    }, headCell.label, orderBy === headCell.id ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, order === 'desc' ? /*#__PURE__*/React__default.createElement("i", {
      className: "meta-crm-icon-ic_arrow-down font-size-20",
      style: {
        color: '#A5A5A5'
      }
    }) : /*#__PURE__*/React__default.createElement("i", {
      className: "meta-crm-icon-ic_arrow-up font-size-20",
      style: {
        color: '#A5A5A5'
      }
    })) : /*#__PURE__*/React__default.createElement("i", {
      className: "meta-crm-icon-ic_arrow-default font-size-20",
      style: {
        color: '#A5A5A5'
      }
    })), headCell.labelContent && isLoading ? /*#__PURE__*/React__default.createElement(StyledSkeleton, {
      variant: "rectangular",
      animation: "wave",
      sx: {
        height: '24px',
        margin: '8px 0'
      }
    }) : headCell.labelContent);
  }))), isLoading ? /*#__PURE__*/React__default.createElement(StyledTableBody, null, Array.from({
    length: loadingIndicatorAmount
  }).map((_, rowIndex) => /*#__PURE__*/React__default.createElement(StyledTableRow, {
    key: "skeleton-row-".concat(rowIndex)
  }, checkbox && /*#__PURE__*/React__default.createElement(StyledTableCell, {
    key: "skeleton-cell-checkbox",
    isFixed: hasFixedHeader
  }, /*#__PURE__*/React__default.createElement(StyledSkeleton, {
    variant: "rectangular",
    animation: "wave"
  })), headerItems.map((header, colIndex) => /*#__PURE__*/React__default.createElement(StyledTableCell, {
    key: "skeleton-cell-".concat(colIndex)
  }, /*#__PURE__*/React__default.createElement(StyledSkeleton, {
    variant: "rectangular",
    animation: "wave"
  })))))) : /*#__PURE__*/React__default.createElement(StyledTableBody, {
    noData: contents.length === 0
  }, map(contents, (row, index) => {
    const rowId = rowData.length > 0 ? get(rowData, "[".concat(index, "][").concat(rowKey, "]")) : index;
    const uuid = v4();
    const key = noKey ? uuid : rowId;

    // id + source
    const isSelected = selectedRowData.some(item => item[rowKey] === rowId);
    const isRowLoading = get(rowData, "[".concat(index, "].isRowLoading"), false);
    const isRowPinned = get(rowData, "[".concat(index, "].pinned"), false);
    return /*#__PURE__*/React__default.createElement(StyledTableRow, {
      isSelected: selectedRowData.find(item => item[rowKey] === rowId),
      isPinned: isRowPinned,
      isRowCanClick: (checkbox || onRowClick) && !isRowLoading,
      key: key,
      sx: {
        '&:last-child td, &:last-child th': {
          border: 0
        }
      },
      onClick: event => {
        if (onRowClick && !isRowLoading) {
          onRowClick(rowId, rowData[index], event);
        }
      }
    }, checkbox && /*#__PURE__*/React__default.createElement(StyledTableCell, {
      cellWidth: 40,
      rows: contents.length,
      checkbox: checkbox,
      lastFixedItem: lastFixedItem,
      rowIndex: index,
      isFixed: hasFixedHeader,
      onClick: e => e.stopPropagation()
    }, /*#__PURE__*/React__default.createElement(Box, {
      sx: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    }, /*#__PURE__*/React__default.createElement(Checkbox, {
      mr: 0,
      checked: isSelected,
      onChange: (event, checked) => handleCheck(event, checked, rowData[index])
    }))), /*#__PURE__*/React__default.createElement(CRMTableCell, {
      rowData: row,
      rows: contents.length,
      rowIndex: index,
      isSelected: isSelected,
      headers: headerItems,
      lastFixedItem: lastFixedItem,
      calculateLeftDistance: calculateLeftDistance,
      verticalAlign: verticalAlign
    }), isRowLoading && /*#__PURE__*/React__default.createElement(StyledRowLoading, {
      component: "td",
      rowIndex: index,
      rows: contents.length
    }, rowLoadingText || 'Loading...', /*#__PURE__*/React__default.createElement(Box, null, /*#__PURE__*/React__default.createElement(Box, {
      component: "img",
      src: LoadingImg,
      alt: "loading",
      width: "20px",
      height: "20px"
    }))));
  }), contents.length === 0 && /*#__PURE__*/React__default.createElement(TableRow, null, /*#__PURE__*/React__default.createElement(TableCell, {
    colSpan: checkbox ? get(headerItems, 'length', 0) + 1 : get(headerItems, 'length', 0),
    sx: {
      borderBottom: '0px',
      height: "calc(".concat(get(containerStyle, 'height', '50vh'), " - 40px)"),
      textAlign: 'center',
      color: '#555',
      backgroundColor: 'white'
    }
  }, noDataComponent || /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }
  }, /*#__PURE__*/React__default.createElement(IconIcTableEmpty, {
    width: 60,
    height: 60
  }), /*#__PURE__*/React__default.createElement(Box, {
    sx: {
      fontWeight: 700,
      fontSize: 14,
      marginTop: 2
    }
  }, noDataTitle))))))), setPage && (dataCounts > 0 || isLoading) && /*#__PURE__*/React__default.createElement(StyledTableFoot, null, /*#__PURE__*/React__default.createElement(Box, null, leftFooter), /*#__PURE__*/React__default.createElement(Pagination, {
    rowsPerPage: rowsPerPage,
    dataCounts: dataCounts,
    page: page,
    setPage: setPage,
    pageText: pageText,
    pageOfText: pageOfText
  })));
}

//
CRMTable.propTypes = {
  rowData: PropTypes.arrayOf(PropTypes.shape({})),
  containerStyle: PropTypes.shape({}),
  contents: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
  headers: PropTypes.arrayOf(PropTypes.shape({})),
  selectedRowData: PropTypes.arrayOf(PropTypes.shape({})),
  setSelectedRowData: PropTypes.func,
  onRowClick: PropTypes.func,
  /**
   *  sort order type asc or desc
   */
  order: PropTypes.oneOf(['asc', 'desc']),
  /**
   * sort order target string
   */
  noKey: PropTypes.bool,
  orderBy: PropTypes.string,
  rowKey: PropTypes.string,
  noDataTitle: PropTypes.string,
  checkbox: PropTypes.bool,
  onRequestSort: PropTypes.func,
  noDataComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  rowLoadingText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isDraggable: PropTypes.bool,
  onRemoveHeader: PropTypes.func,
  customTableTop: PropTypes.oneOfType([PropTypes.func, PropTypes.string, PropTypes.node]),
  isLoading: PropTypes.bool,
  LoadingContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  loadingIndicatorAmount: PropTypes.number,
  verticalAlign: PropTypes.string,
  // pagination
  rowsPerPage: PropTypes.number,
  page: PropTypes.number,
  dataCounts: PropTypes.number,
  setPage: PropTypes.func,
  leftFooter: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  pageText: PropTypes.string,
  pageOfText: PropTypes.string
};
CRMTable.defaultProps = {
  rowData: [],
  selectedRowData: [],
  contents: null,
  headers: null,
  order: 'asc',
  orderBy: '',
  rowKey: '_id',
  noKey: false,
  checkbox: false,
  setSelectedRowData: null,
  noDataComponent: null,
  containerStyle: null,
  noDataTitle: 'No data available',
  onRowClick: null,
  rowLoadingText: null,
  onRequestSort: null,
  isDraggable: false,
  onRemoveHeader: null,
  customTableTop: null,
  isLoading: false,
  LoadingContent: null,
  loadingIndicatorAmount: 15,
  verticalAlign: null,
  // pagination
  rowsPerPage: 20,
  page: 1,
  dataCounts: 0,
  setPage: null,
  leftFooter: null,
  pageText: 'Page',
  pageOfText: 'of'
};
export { CRMTable as default };